import { useState } from 'react';
import { ChevronRight, LogOut } from 'lucide-react';
import { Link, useLocation } from 'react-router';
import { useLogout } from '@/features/logout';
import { navLogoUrl } from '@/shared/assets';
import { UI_TEXT } from '@/shared/languages';
import { useThemeStore, type ThemeMode } from '@/shared/stores';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  HevitonWordmark,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarTrigger,
  THEME_ICON,
  useCanHover,
  useSidebar,
} from '@/shared/ui';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@herit-fe/heritage-ui';
import {
  isNavGroup,
  isNavGroupActive,
  isRouteActive,
  NAV_ITEMS,
  type NavGroup,
} from '../model/nav-config';
import { ScrollArea } from '@herit-fe/heritage-ui';

// 테마 토글 = **3단 순차 순환**(라이트 → 다크 → 시스템 → 라이트…). `NEXT_THEME` = 각 모드의 다음 모드(exhaustive
// Record 라 순환 정의가 타입상 완결). 아이콘·라벨은 해석된 다크 여부가 아니라 **현재 모드**를 표시 — 시스템
// 상태도 한눈에 구분되게. 아이콘 매핑은 설정 화면과 공유하는 `THEME_ICON`(shared/ui) SSOT.
const NEXT_THEME: Record<ThemeMode, ThemeMode> = { light: 'dark', dark: 'system', system: 'light' };
const THEME_LABEL: Record<ThemeMode, string> = {
  light: UI_TEXT.SETTINGS_THEME_LIGHT,
  dark: UI_TEXT.SETTINGS_THEME_DARK,
  system: UI_TEXT.SETTINGS_THEME_SYSTEM,
};

/**
 * 데스크탑(lg+) 좌측 네비 — heritage `Sidebar`(`collapsible="icon"`). 펼치면 라벨까지, 접으면 아이콘 레일만
 * 남는다(완전히 사라지지 않음). 토글=헤더 `SidebarTrigger` + 우측 `SidebarRail`. 메뉴 정의는 `nav-config`.
 */
export function AppSidebar() {
  const { pathname } = useLocation();
  const { logout, canLogout } = useLogout();
  const { setOpenMobile, state } = useSidebar();
  // 토글 문구 — 현재 상태가 아니라 **누르면 될 상태**를 말한다(툴팁·aria-label 공용).
  const toggleLabel = state === 'collapsed' ? UI_TEXT.NAV_EXPAND : UI_TEXT.NAV_COLLAPSE;
  const canHover = useCanHover();
  // 모바일 sheet 일 때 nav 이동 후 닫는다(데스크탑 rail 에선 openMobile 미사용이라 무해). 예전 SheetClose 대체.
  const closeMobile = () => setOpenMobile(false);
  const mode = useThemeStore((s) => s.mode);
  const setMode = useThemeStore((s) => s.setMode);
  // 현재 모드 아이콘/라벨 + 클릭 시 다음 모드(순차 순환).
  const ThemeIcon = THEME_ICON[mode];
  return (
    // floating 패널 외곽 — heritage 가 `shadow-sm` + `ring-1 ring-sidebar-border` 를 기본으로 박아둔다.
    // 테두리는 border 가 아니라 **ring** 이라 `border-0` 로는 안 지워지고 `ring-0` 이어야 한다. 둘 다 heritage 의
    // `group-data-[variant=floating]:*` 과 특이도가 같아(0,2,0) `!` 로 확정 오버라이드 — 그림자는 키우고 링은 제거.
    // className 은 sidebar-container(고정 wrapper)로 가므로 자손 선택자로 실제 패널을 잡는다. mobile 표면도
    // `data-sidebar=sidebar` 를 달고 있어 데스크탑 패널만 있는 `data-slot=sidebar-inner` 로 한정.
    //
    // 모바일 표면 = `Drawer`(heritage-ui ≥1.0.13 기본, 스와이프로 닫힘). `border-none` 은 그 Drawer 의
    // `border-r` 을 지우는 용도 — 1.0.13 부터 모바일 분기도 className 을 병합해줘서 도달한다(그 전엔 버려졌다).
    <Sidebar
      collapsible="icon"
      variant="floating"
      className="**:data-[slot=sidebar-inner]:shadow-lg! **:data-[slot=sidebar-inner]:ring-0! **:data-[slot=sidebar-inner]:rounded-2xl! border-none"
    >
      {/* 접힘 시 헤더 하단 패딩을 메뉴 gap(4px)에 맞춰 expander↔첫 메뉴 간격을 균일화(기본 8px→4px).
          상단 = safe-area(노치) + 0.5rem — 모바일 sheet 에서만 유효(데스크탑 rail 은 env=0). */}
      <div className="px-2 pb-2 pt-[calc(env(safe-area-inset-top)+0.5rem)]">
        <SidebarHeader className="group-data-[collapsible=icon]:pb-1">
          {/* 펼침: [로고 HEVITON … ⊞] 가로. 접힘(icon): 세로 스택 — 로고 위 / expander 아래(중앙).
              로고↔expander 는 brand↔control 분리라 약간 넓게(gap-2=8px), expander↔메뉴는 메뉴 리듬(4px). */}
          <div className="flex items-center gap-2 group-data-[collapsible=icon]:flex-col group-data-[collapsible=icon]:items-center group-data-[collapsible=icon]:gap-2 group-data-[collapsible=icon]:px-0">
            <img
              src={navLogoUrl}
              alt=""
              aria-hidden="true"
              className="size-7 shrink-0 transition-all duration-1000"
            />
            {/* 워드마크 — 접힘(rail)에선 아이콘만 남기고 숨긴다. 색은 currentColor 라 테마 토큰을 따라감. */}
            <HevitonWordmark className="text-foreground group-data-[collapsible=icon]:hidden w-20" />
            {/* 토글 — 아이콘만 있는 버튼이라 툴팁으로 뜻을 보충한다. `TooltipTrigger render` 로 버튼을
                **대체 렌더**해 중첩 버튼(button in button)을 피한다. 접힘(rail)에선 툴팁이 사이드바 위로
                겹치지 않게 오른쪽으로 편다. */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger
                  render={
                    <SidebarTrigger
                      aria-label={toggleLabel}
                      className="ml-auto group-data-[collapsible=icon]:ml-0"
                    />
                  }
                />
                <TooltipContent side="right">{toggleLabel}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </SidebarHeader>
      </div>
      <SidebarContent className="flex flex-col min-h-0">
        {/* 접힘 시 그룹 상단 패딩 제거 — 헤더 pb-1 과 합쳐 expander↔메뉴 = 메뉴 간 gap(4px) 동일. */}
        <ScrollArea className="min-h-0" scrollbarVisibility={canHover ? 'hover' : 'scroll'}>
          <SidebarGroup className="group-data-[collapsible=icon]:pt-0">
            <SidebarMenu className="gap-1">
              {NAV_ITEMS.map((item) =>
                isNavGroup(item) ? (
                  <NavGroupItem
                    key={item.key}
                    group={item}
                    pathname={pathname}
                    onNavigate={closeMobile}
                  />
                ) : (
                  // 접힘 레일에선 p-1 제거 — 레일 내부 폭(34px)에서 4px 를 더 깎으면 26px 가 되는데, 버튼은
                  // heritage 의 `size-8!`(32px) + `overflow-hidden`(min-width:0) 이라 flex 아이템으로 찌그러져
                  // 26px 가 된다. padding 은 `p-2!` 로 좌우 8px 고정이라 아이콘이 좌측 기준으로 밀려 중심이
                  // 3px 어긋나고 우측이 잘린다. 접힘 시 패딩 0 → 버튼 32px 유지 → 아이콘 정중앙.
                  <SidebarMenuItem
                    key={item.key}
                    className="flex justify-center items-center p-1 group-data-[collapsible=icon]:p-0"
                  >
                    <SidebarMenuButton
                      render={<Link to={item.route} />}
                      tooltip={item.label}
                      isActive={isRouteActive(item.route, pathname)}
                      className={`h-10 px-4`}
                      onClick={closeMobile}
                    >
                      <item.icon />
                      <span>{item.label}</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ),
              )}
            </SidebarMenu>
          </SidebarGroup>
        </ScrollArea>
      </SidebarContent>
      {/* 푸터 — 로그아웃(노출 판단은 useLogout.canLogout). 접힘 레일에선 아이콘만(tooltip=라벨). */}
      {/* 푸터 하단 = safe-area(홈 인디케이터) + 0.5rem — 모바일 sheet 전용(데스크탑 rail 은 env=0). */}
      {canLogout && (
        <SidebarFooter className="pb-[calc(env(safe-area-inset-bottom)+0.5rem)]">
          <SidebarMenu>
            <SidebarMenuItem className="flex justify-center items-center group-data-[collapsible=icon]:block">
              {/* 접힘 레일에선 부모가 block 이라 32px 버튼이 좌측 정렬(패널 중심 대비 1px 어긋남) — mx-auto 로 중앙. */}
              <SidebarMenuButton
                className="flex-1 group-data-[collapsible=icon]:mx-auto"
                onClick={logout}
                tooltip={UI_TEXT.AUTH_LOGOUT}
              >
                <LogOut />
                <span>{UI_TEXT.AUTH_LOGOUT}</span>
              </SidebarMenuButton>
              {/* 우측 끝 — 테마 3단 순환 토글(라이트→다크→시스템). 접힘 레일에선 heritage 가 자동 숨김. */}
              <SidebarMenuButton
                className="w-auto group-data-[collapsible=icon]:mx-auto"
                onClick={() => setMode(NEXT_THEME[mode])}
                aria-label={`${UI_TEXT.NAV_THEME_TOGGLE}: ${THEME_LABEL[mode]}`}
              >
                <ThemeIcon />
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>
      )}
      {/*<SidebarRail />*/}
    </Sidebar>
  );
}

/** 자식 있는 그룹 — `Collapsible`(제어형: 활성 그룹 기본 펼침). 접힘 레일에서는 heritage CSS 가 자식·셰브론을 숨김. */
function NavGroupItem({
  group,
  pathname,
  onNavigate,
}: {
  group: NavGroup;
  pathname: string;
  onNavigate: () => void;
}) {
  const { state, isMobile } = useSidebar();
  const groupActive = isNavGroupActive(group, pathname);
  const [open, setOpen] = useState(groupActive);
  const [flyoutOpen, setFlyoutOpen] = useState(false);

  // 접힘 레일(데스크탑) — heritage 가 인라인 서브메뉴를 숨기므로, 대신 아이콘 옆 Popover 플라이아웃으로 하위 노출.
  if (state === 'collapsed' && !isMobile) {
    return (
      <SidebarMenuItem className="flex justify-center items-center">
        <Popover open={flyoutOpen} onOpenChange={setFlyoutOpen}>
          <PopoverTrigger
            render={
              <SidebarMenuButton
                tooltip={group.label}
                isActive={groupActive}
                className="h-10 px-4"
              />
            }
          >
            <group.icon />
            <span>{group.label}</span>
          </PopoverTrigger>
          <PopoverContent side="right" align="start" className="w-48 p-1 gap-1">
            <p className="px-2 py-1.5 text-xs font-medium text-muted-foreground">{group.label}</p>
            {group.children.map((child) => {
              const active = isRouteActive(child.route, pathname);
              return (
                <Link
                  key={child.key}
                  to={child.route}
                  aria-current={active ? 'page' : undefined}
                  onClick={() => {
                    setFlyoutOpen(false);
                    onNavigate();
                  }}
                  className={`block rounded-md px-2 py-1.5 text-sm ${
                    active
                      ? 'bg-accent font-medium text-accent-foreground'
                      : 'text-foreground hover:bg-accent/50'
                  }`}
                >
                  {child.label}
                </Link>
              );
            })}
          </PopoverContent>
        </Popover>
      </SidebarMenuItem>
    );
  }

  return (
    <Collapsible
      render={<SidebarMenuItem />}
      open={open}
      onOpenChange={setOpen}
      // 열린 그룹은 하위 항목을 묶어 보이도록 배경을 깐다. base-ui Collapsible 은 열림 시 **`data-open`**
      // 속성을 달므로 `data-[open]:`(=`[data-open]`) 로 잡는다(`data-[state=open]` 아님). light=gray-100,
      // dark 는 sidebar 면(=card, gray-800)보다 한 스텝 **밝게**(gray-700) 띄워 같은 "떠 있는 묶음" 인상.
      className="group/collapsible rounded-lg data-[open]:bg-gray-50 dark:data-[open]:bg-gray-700 group-data-[collapsible=icon]:bg-transparent! p-1"
    >
      <CollapsibleTrigger
        render={<SidebarMenuButton tooltip={group.label} />}
        className="h-10 px-4"
      >
        <group.icon />
        <span className={groupActive ? 'font-bold' : ''}>{group.label}</span>
        <ChevronRight
          className={`ml-auto transition-transform duration-200 group-data-[collapsible=icon]:hidden ${
            open ? 'rotate-90' : ''
          }`}
        />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <SidebarMenuSub className="m-0 p-0 border-none py-0 pt-1">
          {group.children.map((child) => (
            <SidebarMenuSubItem key={child.key}>
              <SidebarMenuSubButton
                render={<Link to={child.route} />}
                isActive={isRouteActive(child.route, pathname)}
                className="h-10 px-4 pl-10"
                onClick={onNavigate}
              >
                {child.label}
              </SidebarMenuSubButton>
            </SidebarMenuSubItem>
          ))}
        </SidebarMenuSub>
      </CollapsibleContent>
    </Collapsible>
  );
}
